import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardList, Home as HomeIcon, BadgeCheck, ShieldCheck, CalendarCheck, MapPin, Quote, Receipt } from "lucide-react";
import { api, money } from "@/lib/api";
import { useSettings, useSeo } from "@/context/SettingsContext";
import { HERO_IMG, LAWN_IMG } from "@/lib/constants";
import { SectionTitle, ProductCard, FinalCta, Eyebrow } from "@/components/site/Blocks";
import { FaqList, faqItems } from "@/pages/Faq";

export const STEPS = [
  [ClipboardList, "Request Your Trial", "Complete the short booking form with your contact details and preferred trial date."],
  [HomeIcon, "Test It at Home", "Our team will confirm your request and arrange delivery or pickup according to service availability."],
  [BadgeCheck, "Buy It or Arrange a Return", "Keep the mower and proceed with the purchase if you like it. Otherwise, follow the disclosed return and collection procedure."],
];

export function Steps() {
  return (
    <div className="grid gap-6 md:grid-cols-3">
      {STEPS.map(([Icon, t, d], i) => (
        <div key={t} data-testid={`how-step-${i + 1}`} className="relative rounded-2xl border border-slate-200 bg-white p-8 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-emerald-900/5">
          <span className="font-display text-6xl font-black text-emerald-100">0{i + 1}</span>
          <div className="mt-2 grid h-12 w-12 place-items-center rounded-xl bg-emerald-700 text-white"><Icon className="h-6 w-6" /></div>
          <p className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">Step {i + 1}</p>
          <h3 className="mt-2 font-display text-xl font-bold text-slate-900">{t}</h3>
          <p className="mt-3 leading-relaxed text-slate-600">{d}</p>
        </div>
      ))}
    </div>
  );
}

function Hero() {
  const { settings } = useSettings();
  return (
    <section className="relative isolate min-h-[640px] overflow-hidden bg-slate-900">
      <img src={HERO_IMG} alt="Homeowner mowing a green lawn" className="absolute inset-0 -z-10 h-full w-full object-cover" fetchPriority="high" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-slate-950/90 via-slate-900/65 to-slate-900/10" />
      <div className="mx-auto max-w-7xl px-4 pb-20 pt-24 sm:px-6 sm:pt-32 lg:px-8">
        <div className="max-w-2xl animate-in fade-in slide-in-from-bottom-6 duration-700">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-emerald-300 backdrop-blur-md">At-home mower trials · USA</span>
          <h1 data-testid="hero-headline" className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Try a Lawn Mower on Your Own Lawn for Just <span className="text-emerald-400">{money(settings.trial_fee).replace(".00", "")}</span>
          </h1>
          <p className="mt-6 text-base leading-relaxed text-slate-200 md:text-lg">Not sure which mower is right for your yard? Test it at home, experience the cutting performance, and decide whether it's right for you before purchasing.</p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <Link to="/book" data-testid="book-trial-cta-button" className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-emerald-600 px-8 text-base font-bold text-white shadow-lg shadow-emerald-950/40 transition-[background-color,transform] hover:scale-[1.02] hover:bg-emerald-500">Book My {money(settings.trial_fee).replace(".00", "")} Trial <ArrowRight className="h-5 w-5" /></Link>
            <Link to="/how-it-works" data-testid="hero-how-it-works-button" className="inline-flex h-14 items-center justify-center rounded-full border border-white/30 bg-white/10 px-8 text-base font-semibold text-white backdrop-blur-md transition-colors hover:bg-white/20">How It Works</Link>
          </div>
          <div className="mt-10 grid max-w-xl grid-cols-1 gap-3 text-sm text-slate-200 sm:grid-cols-3">
            {[[Receipt, "Fee disclosed upfront"], [CalendarCheck, "Request first, confirm after"], [ShieldCheck, "No charge on submission"]].map(([I, t]) => (
              <p key={t} className="flex items-center gap-2"><I className="h-4 w-4 shrink-0 text-emerald-400" />{t}</p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TrialDetails() {
  const { settings } = useSettings();
  const items = [
    ["Trial fee", money(settings.trial_fee), "Collected only after your trial is confirmed, through a disclosed payment step."],
    ["Trial length", `${settings.trial_days} day${settings.trial_days > 1 ? "s" : ""}`, "Standard period unless a different period is confirmed in writing."],
    ["Delivery / pickup", settings.delivery_options?.length ? settings.delivery_options.map((d) => d[0].toUpperCase() + d.slice(1)).join(" or ") : "By arrangement", "Arranged by availability in your area. Any fees are disclosed before confirmation."],
    ["Decision", "Buy or return", "Purchase at the listed price, or we collect it per the Trial and Return Policy."],
  ];
  return (
    <section className="bg-[#0B130E] py-20 sm:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-5">
          <SectionTitle light eyebrow="The trial, in detail" title="Clear terms. No surprises." text="We'd rather you know exactly what happens before you request a trial. Here's how it works." />
          <img src={LAWN_IMG} alt="Freshly mowed backyard" loading="lazy" className="mt-10 aspect-[4/3] w-full rounded-2xl object-cover" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:col-span-7 lg:self-center">
          {items.map(([k, v, d]) => (
            <div key={k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-7">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-400">{k}</p>
              <p className="mt-3 font-display text-3xl font-bold text-white">{v}</p>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{d}</p>
            </div>
          ))}
          <Link to="/trial-return-policy" data-testid="home-trial-policy-link" className="text-sm font-semibold text-emerald-400 hover:text-emerald-300 sm:col-span-2">Read the full Trial and Return Policy →</Link>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const { settings } = useSettings();
  const t = settings.testimonials || [];
  return (
    <section className="bg-slate-50 py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle eyebrow="Customer reviews" title="What trial customers say" />
        {t.length ? (
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {t.map((r, i) => (
              <figure key={i} data-testid={`testimonial-${i}`} className="rounded-2xl border border-slate-200 bg-white p-8">
                <Quote className="h-8 w-8 text-emerald-600" />
                <blockquote className="mt-4 leading-relaxed text-slate-700">"{r.text}"</blockquote>
                <figcaption className="mt-6 text-sm"><span className="font-bold text-slate-900">{r.name}</span>{r.location && <span className="text-slate-500"> · {r.location}</span>}</figcaption>
              </figure>
            ))}
          </div>
        ) : (
          <div data-testid="testimonials-empty" className="mt-10 max-w-2xl rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-slate-600">
            We only publish genuine reviews from customers who have completed a trial. Reviews will appear here as our first trial customers share their experience.
          </div>
        )}
      </div>
    </section>
  );
}

function ServiceAreas() {
  const { settings } = useSettings();
  return (
    <section className="py-20 sm:py-28">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <SectionTitle eyebrow="Service areas" title="Where we currently offer trials" text="Availability varies by location and mower model. We confirm coverage for your address when we review your request." />
        <div className="flex flex-wrap content-start gap-3" data-testid="service-areas-list">
          {(settings.service_areas || []).map((a) => (
            <span key={a} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 font-semibold text-slate-800"><MapPin className="h-4 w-4 text-emerald-700" />{a}</span>
          ))}
          {!settings.service_areas?.length && <p className="text-slate-500">Service areas will be confirmed on request.</p>}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);
  useSeo("Try a Lawn Mower at Home for $1", "Test a lawn mower on your own lawn for a $1 trial fee. Request a trial, test it at home, then buy it or arrange a return.");
  useEffect(() => { api.get("/public/products").then((r) => setProducts(r.data.filter((p) => p.featured).slice(0, 3))).catch(() => {}); }, []);
  return (
    <>
      <Hero />
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionTitle eyebrow="How it works" title="Three simple steps from request to decision" />
          <div className="mt-12"><Steps /></div>
        </div>
      </section>
      <section className="bg-slate-50 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <SectionTitle eyebrow="Featured mowers" title="Pick the mower you want to test" />
            <Link to="/products" data-testid="home-view-all-products" className="font-semibold text-emerald-800 hover:text-emerald-900">View all mowers →</Link>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">{products.map((p) => <ProductCard key={p.id} p={p} />)}</div>
        </div>
      </section>
      <TrialDetails />
      <Testimonials />
      <ServiceAreas />
      <section className="bg-slate-50 py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-4">
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Common questions</h2>
            <Link to="/faq" className="mt-6 inline-block font-semibold text-emerald-800">See all FAQs →</Link>
          </div>
          <div className="lg:col-span-8"><FaqList items={faqItems(settings)} limit={5} /></div>
        </div>
      </section>
      <FinalCta />
    </>
  );
}
